import type { Tier, TierId, BillingPeriod } from "@mw/shared/pricing";
import { MUSIC_FINETUNE_USD, getSubscriptionPrice, getPricingTiers, getAgentIncludedMinutes } from "@mw/shared/pricing";
import { appUrls } from "@/constants";

// Re-export shared value types and constructors
export type { FormattedValue, CurrencyUnit } from "./pricing-value-types";
export { numberValue, currencyValue, hoursValue, minutesValue, charactersValue } from "./pricing-value-types";
import type { FormattedValue } from "./pricing-value-types";
import { numberValue, currencyValue, hoursValue, minutesValue, charactersValue } from "./pricing-value-types";

export type { BillingPeriod } from "@mw/shared/pricing";

export type ApiPricingSection =
  | "text_to_speech"
  | "speech_to_text"
  | "speech_engine"
  | "music"
  | "voice_isolator"
  | "voice_changer"
  | "sound_effects"
  | "dubbing_v1";

// Translation key types matching en.json structure
export type ApiPricingSectionTranslationKey =
  | "text_to_speech_api"
  | "speech_to_text_api"
  | "speech_engine_api"
  | "music_api"
  | "voice_isolator_api"
  | "voice_changer_api"
  | "sound_effects_api"
  | "dubbing_api";

export type ApiPricingCtaTranslationKey =
  | "get_started_with_tts"
  | "get_started_with_stt"
  | "get_started_with_speech_engine"
  | "get_started_with_music"
  | "get_started_with_voice_isolator"
  | "get_started_with_voice_changer"
  | "get_started_with_sound_effects"
  | "get_started_with_dubbing";

export type ApiPricingRowTranslationKey =
  | "price_per_1k_characters"
  | "characters_included"
  | "price_per_hour"
  | "entity_detection_per_hour"
  | "keyterm_prompting_per_hour"
  | "hours_included"
  | "price_per_minute"
  | "minutes_included"
  | "included_call_per_minute"
  | "additional_call_per_minute"
  | "burst_pricing_call"
  | "concurrent_calls"
  | "number_of_included_generations"
  | "price_per_minute_with_watermark"
  | "included_minutes_watermark"
  | "price_per_minute_without_watermark"
  | "included_minutes_no_watermark"
  | "music_finetune_cost";

export type ApiPricingRowTooltipKey = "burst_pricing_tooltip" | "concurrency_limit_tooltip";

export type ApiPricingSubSectionTranslationKey =
  | "flash_turbo"
  | "multilingual_v2_v3"
  | "scribe_v1_v2"
  | "scribe_v2_realtime";

export interface ApiPricingRow {
  labelKey: ApiPricingRowTranslationKey;
  /** Optional tooltip key under `AgentsPricing.tooltips` (shared copy with Agents pricing). */
  tooltipKey?: ApiPricingRowTooltipKey;
  getValue: (tier: Tier, period: BillingPeriod) => FormattedValue;
}

export interface ApiPricingSectionConfig {
  titleKey: ApiPricingSectionTranslationKey;
  descriptionKey: `${ApiPricingSectionTranslationKey}_description`;
  ctaLabelKey: ApiPricingCtaTranslationKey;
  ctaHref: string;
  subSections: ApiPricingSubSection[];
}

export interface ApiPricingSubSection {
  labelKey?: ApiPricingSubSectionTranslationKey;
  rows: ApiPricingRow[];
}

// Text-to-Speech rows (unit is in label, so use "flat" for currency)
const TTS_MULTILINGUAL_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_1k_characters",
    getValue: tier => currencyValue(tier.platformConfig.api.textToSpeech.multilingualUsdPer1000Chars),
  },
  {
    labelKey: "characters_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.textToSpeech.multilingualUsdPer1000Chars > 0) {
        return charactersValue(Math.round(subscriptionPrice / api.textToSpeech.multilingualUsdPer1000Chars) * 1000);
      }
      return charactersValue(tier.products.textToSpeech.limits.characterLimit);
    },
  },
];

// Flash/Turbo is half the price per character (or double the characters for same price)
const TTS_FLASH_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_1k_characters",
    getValue: tier => currencyValue(tier.platformConfig.api.textToSpeech.turboUsdPer1000Chars),
  },
  {
    labelKey: "characters_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.textToSpeech.turboUsdPer1000Chars > 0) {
        return charactersValue(Math.round(subscriptionPrice / api.textToSpeech.turboUsdPer1000Chars) * 1000);
      }
      const characterLimit = tier.products.textToSpeech.limits.characterLimit;
      return charactersValue(characterLimit !== undefined ? characterLimit * 2 : undefined);
    },
  },
];

// Speech-to-Text rows
const STT_SCRIBE_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_hour",
    getValue: tier => currencyValue(tier.platformConfig.api.speechToText.scribeUsdPerHour),
  },
  {
    labelKey: "entity_detection_per_hour",
    getValue: tier => currencyValue(tier.platformConfig.api.speechToText.entityDetectionUsdPerHour, undefined, 3),
  },
  {
    labelKey: "keyterm_prompting_per_hour",
    getValue: tier => currencyValue(tier.platformConfig.api.speechToText.keytermPromptingUsdPerHour, undefined, 3),
  },
  {
    labelKey: "hours_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.speechToText.scribeUsdPerHour > 0) {
        return hoursValue(Math.round(subscriptionPrice / api.speechToText.scribeUsdPerHour));
      }
      return hoursValue(tier.products.speechToText.limits.apiHours);
    },
  },
];

/** Same tier math as Agents "Usage & Scale" (tier.platformConfig.agents). */
const SPEECH_ENGINE_CURRENCY_PRECISION = 3;

const getAgentsAdditionalCallPerMinute = (tier: Tier): number | undefined =>
  tier.platformConfig.agents.audioBurstUsdPerMinute ?? undefined;

const SPEECH_ENGINE_ROWS: ApiPricingRow[] = [
  {
    labelKey: "included_call_per_minute",
    getValue: tier => minutesValue(getAgentIncludedMinutes(tier)),
  },
  {
    labelKey: "additional_call_per_minute",
    getValue: tier => currencyValue(getAgentsAdditionalCallPerMinute(tier), "flat", SPEECH_ENGINE_CURRENCY_PRECISION),
  },
  {
    labelKey: "burst_pricing_call",
    tooltipKey: "burst_pricing_tooltip",
    getValue: tier => {
      const base = tier.platformConfig.agents.audioBurstUsdPerMinute;
      if (base == null) return { type: "unavailable" };
      return currencyValue(base * 2, "flat", SPEECH_ENGINE_CURRENCY_PRECISION);
    },
  },
  {
    labelKey: "concurrent_calls",
    tooltipKey: "concurrency_limit_tooltip",
    getValue: tier => numberValue(tier.platformConfig.agents.concurrencyLimit),
  },
];

const STT_REALTIME_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_hour",
    getValue: tier => currencyValue(tier.platformConfig.api.speechToText.scribeRealtimeUsdPerHour),
  },
  {
    labelKey: "hours_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.speechToText.scribeRealtimeUsdPerHour > 0) {
        return hoursValue(Math.round(subscriptionPrice / api.speechToText.scribeRealtimeUsdPerHour));
      }
      return hoursValue(tier.products.speechToTextRealtime.limits.includedHours);
    },
  },
];

// Other product rows
const MUSIC_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_minute",
    getValue: tier => currencyValue(tier.platformConfig.api.music.usdPerMinute, undefined, 3),
  },
  {
    labelKey: "minutes_included",
    getValue: tier => minutesValue(tier.products.music.limits.includedMinutes),
  },
  {
    labelKey: "music_finetune_cost",
    getValue: () => currencyValue(MUSIC_FINETUNE_USD, "flat"),
  },
];

const VOICE_ISOLATOR_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_minute",
    getValue: tier => currencyValue(tier.platformConfig.api.voiceIsolator.usdPerMinute, undefined, 3),
  },
  {
    labelKey: "minutes_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.voiceIsolator.usdPerMinute > 0) {
        return minutesValue(Math.round(subscriptionPrice / api.voiceIsolator.usdPerMinute));
      }
      return minutesValue(tier.products.voiceIsolator.limits.includedMinutes);
    },
  },
];

const VOICE_CHANGER_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_minute",
    getValue: tier => currencyValue(tier.platformConfig.api.voiceChanger.usdPerMinute, undefined, 3),
  },
  {
    labelKey: "minutes_included",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.voiceChanger.usdPerMinute > 0) {
        return minutesValue(Math.round(subscriptionPrice / api.voiceChanger.usdPerMinute));
      }
      return minutesValue(tier.products.voiceChanger.limits.includedMinutes);
    },
  },
];

const SOUND_EFFECTS_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_minute",
    getValue: tier => currencyValue(tier.platformConfig.api.soundEffects.usdPerMinute, undefined, 3),
  },
  {
    labelKey: "number_of_included_generations",
    getValue: tier => numberValue(tier.products.soundEffects.limits.includedGenerations),
  },
];

const DUBBING_V1_ROWS: ApiPricingRow[] = [
  {
    labelKey: "price_per_minute_with_watermark",
    getValue: tier => currencyValue(tier.platformConfig.api.dubbing.autoUsdPerMinute),
  },
  {
    labelKey: "included_minutes_watermark",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.dubbing.autoUsdPerMinute > 0) {
        return minutesValue(Math.round(subscriptionPrice / api.dubbing.autoUsdPerMinute));
      }
      return minutesValue(tier.products.dubbing.limits.automaticWatermarkMinutes);
    },
  },
  {
    labelKey: "price_per_minute_without_watermark",
    getValue: tier => currencyValue(tier.platformConfig.api.dubbing.autoNoWatermarkUsdPerMinute),
  },
  {
    labelKey: "included_minutes_no_watermark",
    getValue: tier => {
      const api = tier.platformConfig.api;
      const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
      if (subscriptionPrice && api.dubbing.autoNoWatermarkUsdPerMinute > 0) {
        return minutesValue(Math.round(subscriptionPrice / api.dubbing.autoNoWatermarkUsdPerMinute));
      }
      return minutesValue(tier.products.dubbing.limits.automaticNoWatermarkMinutes);
    },
  },
];

// Section configurations
export const API_PRICING_SECTIONS: Record<ApiPricingSection, ApiPricingSectionConfig> = {
  text_to_speech: {
    titleKey: "text_to_speech_api",
    descriptionKey: "text_to_speech_api_description",
    ctaLabelKey: "get_started_with_tts",
    ctaHref: appUrls.textToSpeech,
    subSections: [
      {
        labelKey: "flash_turbo",
        rows: TTS_FLASH_ROWS,
      },
      {
        labelKey: "multilingual_v2_v3",
        rows: TTS_MULTILINGUAL_ROWS,
      },
    ],
  },
  speech_to_text: {
    titleKey: "speech_to_text_api",
    descriptionKey: "speech_to_text_api_description",
    ctaLabelKey: "get_started_with_stt",
    ctaHref: appUrls.speechToText,
    subSections: [
      {
        labelKey: "scribe_v1_v2",
        rows: STT_SCRIBE_ROWS,
      },
      {
        labelKey: "scribe_v2_realtime",
        rows: STT_REALTIME_ROWS,
      },
    ],
  },
  speech_engine: {
    titleKey: "speech_engine_api",
    descriptionKey: "speech_engine_api_description",
    ctaLabelKey: "get_started_with_speech_engine",
    ctaHref: appUrls.speechEngine,
    subSections: [{ rows: SPEECH_ENGINE_ROWS }],
  },
  music: {
    titleKey: "music_api",
    descriptionKey: "music_api_description",
    ctaLabelKey: "get_started_with_music",
    ctaHref: appUrls.music,
    subSections: [
      {
        rows: MUSIC_ROWS,
      },
    ],
  },
  voice_isolator: {
    titleKey: "voice_isolator_api",
    descriptionKey: "voice_isolator_api_description",
    ctaLabelKey: "get_started_with_voice_isolator",
    ctaHref: appUrls.voiceIsolator,
    subSections: [
      {
        rows: VOICE_ISOLATOR_ROWS,
      },
    ],
  },
  voice_changer: {
    titleKey: "voice_changer_api",
    descriptionKey: "voice_changer_api_description",
    ctaLabelKey: "get_started_with_voice_changer",
    ctaHref: appUrls.voiceChanger,
    subSections: [
      {
        rows: VOICE_CHANGER_ROWS,
      },
    ],
  },
  sound_effects: {
    titleKey: "sound_effects_api",
    descriptionKey: "sound_effects_api_description",
    ctaLabelKey: "get_started_with_sound_effects",
    ctaHref: appUrls.soundEffects,
    subSections: [
      {
        rows: SOUND_EFFECTS_ROWS,
      },
    ],
  },
  dubbing_v1: {
    titleKey: "dubbing_api",
    descriptionKey: "dubbing_api_description",
    ctaLabelKey: "get_started_with_dubbing",
    ctaHref: appUrls.dubbing,
    subSections: [
      {
        rows: DUBBING_V1_ROWS,
      },
    ],
  },
};

// Default tiers (excludes enterprise)
export const DEFAULT_API_PRICING_TIERS: TierId[] = ["payg", "starter", "creator", "pro", "scale", "scale_2024_08_10"];

// Section display order
export const API_PRICING_SECTION_ORDER: ApiPricingSection[] = [
  "text_to_speech",
  "speech_to_text",
  "speech_engine",
  "music",
  "voice_isolator",
  "voice_changer",
  "sound_effects",
  "dubbing_v1",
];

// Reference tier for model pricing (Scale tier represents API usage pricing)
const getReferenceTier = () => getPricingTiers().find(t => t.planId === "scale_2024_08_10")!;

/** Helper to extract numeric price from a row's getValue result */
const extractPrice = (row: ApiPricingRow, period: BillingPeriod): number => {
  const value = row.getValue(getReferenceTier(), period);
  return value.type === "currency" && value.value !== undefined ? value.value : 0;
};

/** Configuration mapping model IDs to their pricing rows and the label key for the primary price */
const MODEL_PRICE_CONFIG: Record<string, { rows: ApiPricingRow[]; priceRowLabelKey: ApiPricingRowTranslationKey }> = {
  flash_turbo: { rows: TTS_FLASH_ROWS, priceRowLabelKey: "price_per_1k_characters" },
  multilingual_v2_v3: { rows: TTS_MULTILINGUAL_ROWS, priceRowLabelKey: "price_per_1k_characters" },
  scribe_v1_v2: { rows: STT_SCRIBE_ROWS, priceRowLabelKey: "price_per_hour" },
  scribe_v2_realtime: { rows: STT_REALTIME_ROWS, priceRowLabelKey: "price_per_hour" },
  speech_engine: { rows: SPEECH_ENGINE_ROWS, priceRowLabelKey: "additional_call_per_minute" },
  music: { rows: MUSIC_ROWS, priceRowLabelKey: "price_per_minute" },
  voice_isolator: { rows: VOICE_ISOLATOR_ROWS, priceRowLabelKey: "price_per_minute" },
  voice_changer: { rows: VOICE_CHANGER_ROWS, priceRowLabelKey: "price_per_minute" },
  sound_effects: { rows: SOUND_EFFECTS_ROWS, priceRowLabelKey: "price_per_minute" },
  dubbing_v1: { rows: DUBBING_V1_ROWS, priceRowLabelKey: "price_per_minute_with_watermark" },
};

/**
 * Get the price for a model card based on billing period.
 * Reuses the same pricing logic from the API pricing table rows.
 * Finds the correct row by labelKey rather than assuming array index.
 */
export const getModelPrice = (modelId: string, period: BillingPeriod): number => {
  const config = MODEL_PRICE_CONFIG[modelId];
  if (!config) return 0;

  const priceRow = config.rows.find(row => row.labelKey === config.priceRowLabelKey);
  if (!priceRow) return 0;

  return extractPrice(priceRow, period);
};
